import { Dispatch, SetStateAction } from 'react';

import { isEmpty, isNil, isUndefined } from 'lodash';

export const validateRequiredInputsFilled = (
    input: any,
    setErrorMessage: Dispatch<SetStateAction<any>>,
    errorMessages: any,
    ...requiredFields: string[]
) => {
    Object.keys(errorMessages).forEach((key) => {
        errorMessages[key] = null;
    });
    const emptyFieldErrorMsg = 'This field is required';
    requiredFields.forEach((f: string) => {
        errorMessages[f] = input[f] ? '' : emptyFieldErrorMsg;
    });
    setErrorMessage({ ...errorMessages });
    return Object.values(errorMessages).every((value) => !value);
};

export const validateStartDateBeforeEndDate = (
    input: any,
    startDateFieldName: string,
    endDateFieldName: string,
    setErrorMessage: Dispatch<SetStateAction<any>>,
    errorMessages: any
): boolean => {
    Object.keys(errorMessages).forEach((key) => {
        errorMessages[key] = null;
    });
    if (!input[endDateFieldName]) {
        return true;
    }
    if (input[startDateFieldName] && input[startDateFieldName] < input[endDateFieldName]) {
        return true;
    }
    errorMessages[endDateFieldName] = 'Start Date after End Date';
    setErrorMessage({ ...errorMessages });
    return false;
};

export const onAttributeChangeHandler = (
    value: string,
    attributeName: string,
    attributes: any,
    setAttributes: Dispatch<SetStateAction<any>>
): void => {
    attributes[attributeName] = value;
    setAttributes({
        ...attributes
    });
};

export const validateRequired = (value: string | null | undefined) => {
    if (isNil(value)) return false;
    return !!value.toString().trim();
};

export const validateMinLength = (value: string | undefined, min: number) => {
    if (isUndefined(value)) return false;
    return value.toString().trim().length >= min;
};

export const validateMaxLength = (value: string | undefined, max: number) => {
    if (isUndefined(value)) return false;
    return value.toString().trim().length <= max;
};

export const validateExactLength = (value: string | undefined, length: number) => {
    if (isUndefined(value)) return false;
    return value.toString().trim().length === length;
};

// expect input format: YYYY-MM-DD
export const validateDateString = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    return !isNaN(Date.parse(value));
};

/**
 * Validates a nullable YYYY-MM-DD date string
 *
 * @param value value
 * @returns boolean
 */
export const isValidNullableDateString = (value: string | null | undefined): boolean =>
    isEmpty(value) || validateDateString(value as string);

/**
 * Validates if start date is before end date. This assumes that both start and end dates are valid dates
 *
 * @param startDate start date as string
 * @param endDate end date as string
 * @returns if start date is before end date
 */
export const isStartDateBeforeEndDate = (startDate: string, endDate: string): boolean => {
    const startDateAsMs = Date.parse(startDate);
    const endDateAsMs = Date.parse(endDate);

    return startDateAsMs <= endDateAsMs;
};

export const validateYearMonthFormat = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    // Regular expression pattern to match YYYYMM format
    const pattern = /^\d{4}(0[1-9]|1[0-2])$/;

    // Test the input against the pattern
    return pattern.test(value);
};

export const validateAlphanumericAndUnderscore = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    return /^[a-zA-Z0-9_]+$/.test(value);
};

export const validateAlphanumericCapitalizedAndUnderscore = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    return /^[A-Z0-9_]+$/.test(value);
};

export const validateAlphanumericAndSpace = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    return /^[a-zA-Z0-9 ]+$/.test(value);
};

export const validateAlphanumeric = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    return /^[a-zA-Z0-9]+$/.test(value);
};

export const validateNumeric = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    return !isNaN(Number(value));
};

export const validateNumberRange = (
    value: string | number,
    min: number,
    isMinInclusive: boolean,
    max: number,
    isMaxInclusive: boolean
) => {
    if (isUndefined(value)) return false;
    let numberVal;
    if (typeof value === 'string') {
        numberVal = parseFloat(value);
        if (isNaN(numberVal)) return false;
    } else {
        numberVal = value;
    }
    if (isMinInclusive && isMaxInclusive) {
        return numberVal >= min && numberVal <= max;
    } else if (isMinInclusive && !isMaxInclusive) {
        return numberVal >= min && numberVal < max;
    } else if (!isMinInclusive && isMaxInclusive) {
        return numberVal > min && numberVal <= max;
    } else {
        return numberVal > min && numberVal < max;
    }
};

export const validateNumberDecimalPosition = (
    value: string | number | undefined,
    maximumAllowedDecimalPosition: number
) => {
    if (isUndefined(value)) return false;
    let stringVal = '';
    if (typeof value === 'string') {
        const numberVal = parseFloat(value);
        if (isNaN(numberVal)) return false;
        stringVal = value;
    } else {
        stringVal = value.toString();
    }
    if (!stringVal.includes('.')) {
        return maximumAllowedDecimalPosition >= 0;
    }
    stringVal = stringVal.replace(/^0+|0+$/, '');
    if (stringVal.indexOf('.') === 0) return stringVal.length - 1 <= maximumAllowedDecimalPosition;
    return stringVal.length - stringVal.indexOf('.') - 1 <= maximumAllowedDecimalPosition;
};

export const validateIsFraction = (value: string | undefined) => {
    if (isUndefined(value)) return false;
    if (value === '1') return true;
    const fractionRegex = /^(\d+)\/(\d+)$/;
    const match = value.match(fractionRegex);
    if (!match) return false;
    const [, numerator, denominator] = match;
    if (numerator.startsWith('0') || denominator.startsWith('0')) {
        return false;
    }
    return Math.abs(parseInt(numerator)) <= Math.abs(parseInt(denominator));
};

export const hasErrorMessage = (errorMessages: Record<string, string>) => {
    return Object.keys(errorMessages).some((key) => errorMessages[key] !== '');
};

export const isEverySectionValid = (obj: Record<string, boolean>) => {
    return Object.keys(obj).every((key) => obj[key]);
};

/**
 * Determines if a value is a valid territory code
 *
 * @param value String value
 * @param territoryCodes Set of territory codes
 * @returns boolean
 */
export const isValidTerritory = (value: string | undefined, territoryCodes: Set<string>): boolean =>
    !isUndefined(value) && territoryCodes.has(value);

/**
 * Determines if a value is a valid PAPER plan
 *
 * @param value String value
 * @param planNamesSet Set of PAPER plan names
 * @returns boolean
 */
export const isValidPaperPlanName = (value: string | undefined, planNamesSet: Set<string>): boolean =>
    !isUndefined(value) && planNamesSet.has(value);

/**
 * Determines if a value is a valid currency\
 *
 * @param value String value
 * @param currenciesSet Set of currencies
 * @returns boolean
 */
export const isValidCurrency = (value: string | undefined, currenciesSet: Set<string>): boolean =>
    !isUndefined(value) && currenciesSet.has(value);

/**
 * Validates that an external reporting name contains only allowed characters: letters, numbers, +, (, ), _, spaces
 *
 * @param value String value to validate
 * @returns boolean
 */
export const validateReportingNameCharacters = (value: string | undefined): boolean => {
    if (isUndefined(value)) return false;
    // Allow letters, numbers, +, (, ), _, spaces
    return /^[a-zA-Z0-9+()_ ]+$/.test(value);
};
